import { Capacitor } from '@capacitor/core'

// Detection helpers for the "install the app to your home screen" flow. Pure and side-effect
// free so they can be unit-tested; the state machine that uses them lives in pwaInstallStore.ts.
//
// What can and cannot be detected, by platform:
// - Running as the installed app: reliable everywhere (display-mode, plus iOS's navigator.standalone).
// - Chromium (Android + desktop): `beforeinstallprompt` only ever fires while the app is NOT
//   installed, so its absence is the signal; `appinstalled` reports a fresh install.
// - iOS Safari, from a browser tab: nothing. iOS exposes no way to ask whether the home-screen app
//   exists, so the store falls back to the user's own dismissal.

/** Chromium's install-prompt event. Not part of lib.dom, so declared here. */
export interface IBeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

// Any of these display modes means the page is running as an installed app, not a browser tab.
const INSTALLED_DISPLAY_MODES = ['standalone', 'fullscreen', 'minimal-ui']

/** True inside the Capacitor iOS/Android shell, where the PWA install flow does not apply at all. */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform()
}

/** True when the page is running as an installed PWA rather than in a browser tab. */
export function isRunningStandalone(): boolean {
  const isInstalledDisplayMode = INSTALLED_DISPLAY_MODES.some(
    (mode) => window.matchMedia(`(display-mode: ${mode})`).matches
  )
  // iOS Safari predates the display-mode media query for home-screen apps and sets this instead.
  const isIosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return isInstalledDisplayMode || isIosStandalone
}

export function isIosDevice(): boolean {
  const userAgent = navigator.userAgent
  // iPadOS 13+ reports itself as a Mac; the touch points are what give it away.
  const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return /iPhone|iPod|iPad/.test(userAgent) || isIpadOs
}

export function isAndroidDevice(): boolean {
  return /Android/i.test(navigator.userAgent)
}

export function isMobileDevice(): boolean {
  return isIosDevice() || isAndroidDevice()
}

/** `YYYY-MM` in the device's local time — the unit the monthly banner cadence is counted in. */
export function getMonthKey(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${date.getFullYear()}-${month}`
}

// localStorage can throw (Safari private mode, blocked site data). Install state is a convenience,
// never something worth failing over, so a failure reads as "nothing stored" and writes are dropped.
export function readStoredValue(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function writeStoredValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Dropped on purpose — see above.
  }
}

export function removeStoredValue(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Dropped on purpose — see above.
  }
}
